var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import APIHelper from "./APIHelper";
import { GetActiveCountryCookie, SetCrossSubdomainCookie } from "./Helper";
import { FeatureStatus, Rules, UNLIMITED_LOT_AREA_LIMIT_M2 } from "../types";
import { AuthFeatures } from "../enums/AuthFeatures";
// Env Defaults
const appName = process.env.VUE_APP_CONTEXT || 'archistar';
class AuthUser {
    constructor() {
        this.guid = undefined;
        this.id = undefined;
        this.firstName = undefined;
        this.lastName = undefined;
        this.email = undefined;
        this.mobile = undefined;
        this.lastLogin = undefined;
        this.subscriptions = [];
        this.subscriptionString = undefined;
        this.subscriptionHistory = [];
        this.roles = [];
        this.features = [];
        this.onChargebee = false;
        this.canSelfManageChargebee = false;
        this.companies = [];
        this.authCountries = [];
        this.activeCountryCode = undefined;
        this.appAccess = [];
        this.themeSettings = {};
        this.authPromise = undefined;
        this.profession = undefined;
    }
    isArchistarSubscriptionExpired() {
        if (this.appAccess.Archistar) {
            return (this.appAccess.Archistar === 'Expired');
        }
        return false;
    }
    ;
    isArchistarSubscriptionTrial() {
        if (this.appAccess.Archistar) {
            return (this.appAccess.Archistar === 'Trial');
        }
        return false;
    }
    ;
    isArchistarSubscriptionPending() {
        if (this.appAccess.Archistar) {
            return (this.appAccess.Archistar === 'Pending');
        }
        return false;
    }
    ;
    hasBoughtASite() {
        const feat = this.getFeatureInfoToFeatureByName(AuthFeatures["Full Site Details"]);
        return (feat && feat.lstPropertyGuids && feat.lstPropertyGuids.length > 0) || false;
    }
    ;
    //================================================
    // -------------------- Feature Based Auth --------------------
    getFeatureInfoToFeatureByName(featureName) {
        return this.features.find(feature => feature.featureName === featureName);
    }
    ;
    hasAccessToFeatureByName(featureName) {
        var _a;
        return ((_a = this.getFeatureInfoToFeatureByName(featureName)) === null || _a === void 0 ? void 0 : _a.hasAccess) || false;
    }
    ;
    hasAccessToFeatureByNameForLotAreaM2(featureName, lotArea_m2, skipFeatureAccessCheck = false) {
        const featInfo = this.getFeatureInfoToFeatureByName(featureName);
        if (!featInfo)
            return false;
        if (!skipFeatureAccessCheck && !featInfo.hasAccess)
            return false;
        return (!featInfo.lotAreaLimit_m2 ||
            featInfo.lotAreaLimit_m2 == UNLIMITED_LOT_AREA_LIMIT_M2 ||
            lotArea_m2 < featInfo.lotAreaLimit_m2);
    }
    ;
    hasAccessToFeatureByNameForRegion(featureName, asRegionGuid, arrAsLocalityGuids = undefined, asPropertyGuid = undefined, lotArea_m2 = undefined) {
        const feat = this.getFeatureInfoToFeatureByName(featureName);
        if (!feat) {
            return false;
        }
        if (!feat.hasAccess) {
            //User doesnt have access to feature at all
            return false;
        }
        if (feat.hasAllRegions) {
            // User has all regions or region check ignored for this feature
            if (typeof lotArea_m2 == 'undefined')
                return true;
            return this.hasAccessToFeatureByNameForLotAreaM2(featureName, lotArea_m2);
        }
        // Check if site is in list of users override sites
        if (asPropertyGuid && feat.lstPropertyGuids && feat.lstPropertyGuids.includes(asPropertyGuid)) {
            // User has access to this site
            if (typeof lotArea_m2 == 'undefined')
                return true;
            return this.hasAccessToFeatureByNameForLotAreaM2(featureName, lotArea_m2);
        }
        // Check user has access to this region
        if (!feat.lstRegionGuids || !feat.lstRegionGuids.includes(asRegionGuid)) {
            //User doesnt have access to feature in this region
            return false;
        }
        // If user has locality restrictions for this region, check that this locality is included
        if (arrAsLocalityGuids && feat.lstRegionLocalityGuids && feat.lstRegionLocalityGuids[asRegionGuid]) {
            //User has locality guids for this region, check if localities intersect
            if (!Array.isArray(arrAsLocalityGuids)) {
                arrAsLocalityGuids = [arrAsLocalityGuids];
            }
            const featLocs = feat.lstRegionLocalityGuids[asRegionGuid];
            const commonLocs = arrAsLocalityGuids.filter(itLocGuid => featLocs.includes(itLocGuid));
            if (commonLocs.length === 0) {
                //User doesnt have access to feature in this locality
                return false;
            }
        }
        //User has access
        if (typeof lotArea_m2 == 'undefined')
            return true;
        return this.hasAccessToFeatureByNameForLotAreaM2(featureName, lotArea_m2);
    }
    ;
    getFeatureStatusToFeatureByName(featureName) {
        var _a;
        return ((_a = this.getFeatureInfoToFeatureByName(featureName)) === null || _a === void 0 ? void 0 : _a.featureStatus) || FeatureStatus.DISABLED;
    }
    ;
    getFeatureStatusToFeatureByNameForRegion(featureName, asRegionGuid, arrAsLocalityGuids = undefined, asPropertyGuid = undefined, lotArea_m2 = undefined) {
        const feat = this.getFeatureInfoToFeatureByName(featureName);
        if (!feat) {
            return FeatureStatus.DISABLED;
        }
        if (feat.featureStatus !== FeatureStatus.ENABLED) {
            //User doesnt have access to feature at all
            return feat.featureStatus;
        }
        if (feat.hasAllRegions) {
            // User has all regions or region check ignored for this feature
            if (typeof lotArea_m2 == 'undefined')
                return feat.featureStatus;
            return this.hasAccessToFeatureByNameForLotAreaM2(featureName, lotArea_m2) ? feat.featureStatus : FeatureStatus.DISABLED;
        }
        // Check if site is in list of users override sites
        if (asPropertyGuid && feat.lstPropertyGuids && feat.lstPropertyGuids.includes(asPropertyGuid)) {
            // User has access to this site
            if (typeof lotArea_m2 == 'undefined')
                return feat.featureStatus;
            return this.hasAccessToFeatureByNameForLotAreaM2(featureName, lotArea_m2) ? feat.featureStatus : FeatureStatus.DISABLED;
        }
        // Check user has access to this region
        if (!feat.lstRegionGuids || !feat.lstRegionGuids.includes(asRegionGuid)) {
            //User doesnt have access to feature in this region
            return FeatureStatus.DISABLED;
        }
        // If user has locality restrictions for this region, check that this locality is included
        if (arrAsLocalityGuids && feat.lstRegionLocalityGuids && feat.lstRegionLocalityGuids[asRegionGuid]) {
            //User has locality guids for this region, check if localities intersect
            if (!Array.isArray(arrAsLocalityGuids)) {
                arrAsLocalityGuids = [arrAsLocalityGuids];
            }
            const featLocs = feat.lstRegionLocalityGuids[asRegionGuid];
            const commonLocs = arrAsLocalityGuids.filter(itLocGuid => featLocs.includes(itLocGuid));
            if (commonLocs.length === 0) {
                //User doesnt have access to feature in this locality
                return FeatureStatus.DISABLED;
            }
        }
        //User has access
        if (typeof lotArea_m2 == 'undefined')
            return feat.featureStatus;
        return this.hasAccessToFeatureByNameForLotAreaM2(featureName, lotArea_m2) ? feat.featureStatus : FeatureStatus.DISABLED;
    }
    ;
    isFeatureHidden(featureName) {
        return this.getFeatureStatusToFeatureByName(featureName) == FeatureStatus.HIDDEN;
    }
    ;
    canReadFeatureByName(featureName) {
        var _a;
        return this.hasAccessToFeatureByName(featureName) && ((_a = this.getFeatureInfoToFeatureByName(featureName)) === null || _a === void 0 ? void 0 : _a.rule.includes(Rules.Read)) || false;
    }
    ;
    canWriteFeatureByName(featureName) {
        var _a;
        return this.hasAccessToFeatureByName(featureName) && ((_a = this.getFeatureInfoToFeatureByName(featureName)) === null || _a === void 0 ? void 0 : _a.rule.includes(Rules.Write)) || false;
    }
    ;
    canFeatureByName(rule, featureName) {
        var _a;
        return this.hasAccessToFeatureByName(featureName) && ((_a = this.getFeatureInfoToFeatureByName(featureName)) === null || _a === void 0 ? void 0 : _a.rule.includes(rule)) || false;
    }
    ;
    //================================================
    // -------------------- Resource Based Auth --------------------
    getFeatureInfoToResource(resourceName) {
        return this.features.filter(feature => feature.resource === resourceName);
    }
    ;
    getFeatureNamesForResource(resourceName) {
        return this.features.filter(feature => feature.resource === resourceName).map(feature => feature.featureName);
    }
    ;
    hasAccessToResource(resourceName) {
        return this.getFeatureNamesForResource(resourceName)
            .map(featName => this.hasAccessToFeatureByName(featName))
            .includes(true);
    }
    ;
    hasAccessToResourceForRegion(resourceName, asRegionGuid, arrAsLocalityGuids = undefined, asPropertyGuid = undefined) {
        return this.getFeatureNamesForResource(resourceName)
            .map(featName => this.hasAccessToFeatureByNameForRegion(featName, asRegionGuid, arrAsLocalityGuids, asPropertyGuid))
            .includes(true);
    }
    ;
    getFeatureStatusToResource(resourceName) {
        const featStatues = this.getFeatureNamesForResource(resourceName)
            .map(featName => this.getFeatureStatusToFeatureByName(featName));
        if (featStatues.includes(FeatureStatus.ENABLED))
            return FeatureStatus.ENABLED;
        if (featStatues.includes(FeatureStatus.HIDDEN))
            return FeatureStatus.HIDDEN;
        if (featStatues.includes(FeatureStatus.DISABLED))
            return FeatureStatus.DISABLED;
        return FeatureStatus.DISABLED;
    }
    ;
    getFeatureStatusToResourceForRegion(resourceName, asRegionGuid, arrAsLocalityGuids = undefined, asPropertyGuid = undefined) {
        const featStatues = this.getFeatureNamesForResource(resourceName)
            .map(featName => this.getFeatureStatusToFeatureByNameForRegion(featName, asRegionGuid, arrAsLocalityGuids, asPropertyGuid));
        if (featStatues.includes(FeatureStatus.ENABLED))
            return FeatureStatus.ENABLED;
        if (featStatues.includes(FeatureStatus.HIDDEN))
            return FeatureStatus.HIDDEN;
        if (featStatues.includes(FeatureStatus.DISABLED))
            return FeatureStatus.DISABLED;
        return FeatureStatus.DISABLED;
    }
    ;
    isResourceHidden(resourceName) {
        return this.getFeatureNamesForResource(resourceName)
            .map(featName => this.isFeatureHidden(featName))
            .includes(true);
    }
    ;
    canReadResource(resourceName) {
        return this.getFeatureNamesForResource(resourceName)
            .map(featName => this.canReadFeatureByName(featName))
            .includes(true);
    }
    ;
    canWriteResource(resourceName) {
        return this.getFeatureNamesForResource(resourceName)
            .map(featName => this.canWriteFeatureByName(featName))
            .includes(true);
    }
    ;
    canResource(rule, resourceName) {
        return this.getFeatureNamesForResource(resourceName)
            .map(featName => this.canFeatureByName(rule, featName))
            .includes(true);
    }
    ;
    //================================================
    exists() {
        return this.guid ? true : false;
    }
    fetch(to = undefined, forceNew = false) {
        // Check State for a Promise in progress
        if (this.authPromise && !forceNew) {
            // Return existing promise to prevent duplicate API calls
            return this.authPromise;
        }
        else {
            // Promise Start
            const promise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                try {
                    const userInfo = (_a = (yield APIHelper.AuthAPI.post(`/user/getappuserinfo?app_name=${appName}`))) === null || _a === void 0 ? void 0 : _a.data;
                    // Update user info
                    this.guid = userInfo.userData.guid;
                    this.id = userInfo.userData.id;
                    this.firstName = userInfo.userData.first_name;
                    this.lastName = userInfo.userData.last_name;
                    this.email = userInfo.userData.email;
                    this.mobile = userInfo.userData.mobile;
                    this.subscriptions = userInfo.userData.subscriptions;
                    this.subscriptionString = userInfo.userData.subscriptionString;
                    this.subscriptionHistory = userInfo.userData.subscriptionHistory;
                    this.lastLogin = userInfo.userData.last_login;
                    this.companies = userInfo.userData.companies;
                    this.authCountries = userInfo.userData.authCountries;
                    this.activeCountryCode = GetActiveCountryCookie();
                    this.appAccess = userInfo.userData.app_access;
                    this.profession = userInfo.userData.profession;
                    this.onChargebee = userInfo.userData.on_chargebee;
                    this.canSelfManageChargebee = 'can_self_manage_chargebee' in userInfo.userData ? userInfo.userData.can_self_manage_chargebee : true;
                    // Update user themeSettings
                    this.themeSettings = userInfo.themeSettings;
                    // Update user roles
                    this.roles = userInfo.userRoles;
                    // Update user features
                    this.features = userInfo.features;
                    if (to && to.query.asCtryCode &&
                        (!this.authCountries ||
                            this.authCountries.length == 0 ||
                            this.authCountries.includes(to.query.asCtryCode))) {
                        this.setActiveCountry(to.query.asCtryCode);
                    }
                    const countryCode = (to && ('clearAsCtryCode' in to.query) ? null : this.activeCountryCode);
                    if (this.authCountries &&
                        this.authCountries.length > 0 &&
                        (!countryCode || !this.authCountries.includes(countryCode))) {
                        if (this.authCountries.includes('au'))
                            this.setActiveCountry('au');
                        else
                            this.setActiveCountry(this.authCountries[0]);
                    }
                    // Promise Ended - Erase State
                    this.authPromise = undefined;
                    resolve(userInfo);
                }
                catch (error) {
                    // Promise Ended - Erase State
                    this.authPromise = undefined;
                    reject(error);
                }
            }));
            // Allocating promise to state, so it can be checked above, to prevent duplicate calls
            this.authPromise = promise;
            return promise;
        }
    }
    //================================================
    getActiveCountry() {
        return this.activeCountryCode;
    }
    setActiveCountry(newCountryCode) {
        this.activeCountryCode = newCountryCode;
        SetCrossSubdomainCookie('asCountryCode', newCountryCode, 14);
        APIHelper.AuthAPIs.forEach((itApi) => {
            itApi.setHeaderCommons('X-as-country-code', newCountryCode);
        });
    }
    isActiveCountryOutOfSync() {
        return (this.getActiveCountry() !== GetActiveCountryCookie());
    }
    getActiveCityMode() {
        return APIHelper.activeCityMode;
    }
    setActiveCityMode(newCityMode) {
        APIHelper.activeCityMode = newCityMode;
        if (newCityMode) {
            const tempArrCityMode = newCityMode.split('|');
            this.setActiveCountry(tempArrCityMode[1]);
        }
        APIHelper.AuthAPIs.forEach((itApi) => {
            itApi.setHeaderCommons('X-as-city-mode', newCityMode);
        });
    }
}
export default new AuthUser();
export { AuthUser as AuthUserType };
